import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EXPORTERS_API_CONSTANTS } from '@app/app.constants';
import { LaboratoryBatchExportType } from '@app/modules/laboratory/services/laboratory-batch-export.service';
import {
  ExportDialogDataType,
  ExportType,
  StatusResponse,
} from '@app/modules/service-data/types/service-data.types';
import { environment } from '@root/environments/environment';
import { Observable } from 'rxjs';
import { BlobUriResponseModel, ExporterState } from '../core.types';

@Injectable({
  providedIn: 'root',
})
export class ExportersService {
  private httpClient = inject(HttpClient);

  private exportersApiUrl: string;
  private queryGetCreateBlobUri: string;
  private queryGetReadBlobUri: string;
  private commandStatus = EXPORTERS_API_CONSTANTS.commandStatus;
  private html2pdfUrl = environment.html2pdfUrl;

  constructor() {
    this.exportersApiUrl = environment.exportersApiUrl;
    this.queryGetCreateBlobUri = EXPORTERS_API_CONSTANTS.queryGetCreateBlobUri;
    this.queryGetReadBlobUri = EXPORTERS_API_CONSTANTS.queryGetReadBlobUri;
  }

  public getCreateBlobUri(fileName: string): Observable<BlobUriResponseModel> {
    return this.httpClient.get<BlobUriResponseModel>(
      `${this.exportersApiUrl}/${this.queryGetCreateBlobUri}/${fileName}`
    );
  }

  public getReadBlobUri(fileName: string): Observable<BlobUriResponseModel> {
    return this.httpClient.get<BlobUriResponseModel>(
      `${this.exportersApiUrl}/${this.queryGetReadBlobUri}/${fileName}`
    );
  }

  public getBlob(readUri: string) {
    return this.httpClient.get(readUri);
  }

  public exportSelectedNipts(
    exportDialogDataType: ExportDialogDataType,
    niptIds: string[],
    exportType: ExportType,
    uri: string,
    password?: string,
    anonymous?: boolean
  ) {
    const body: any = { blobUri: uri, laboratory: 'All', password, anonymous };

    if (niptIds && niptIds.length > 0) {
      switch (exportDialogDataType) {
        case ExportDialogDataType.Pii:
          body.productInventoryItemIds = niptIds;
          break;
        case ExportDialogDataType.ServiceData:
          body.serviceDataIds = niptIds;
          break;
        case ExportDialogDataType.Sample:
          body.sampleIds = niptIds;
          break;
        case ExportDialogDataType.FollowUps:
          body.followUpIds = niptIds;
          break;
        case ExportDialogDataType.Reports:
          body.reportIds = niptIds;
          break;
      }
    }

    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${this.getExportCommand(exportType)}`,
      body
    );
  }

  public checkExporterStatus(id: string): Observable<ExporterState> {
    return this.httpClient.get<ExporterState>(
      `${this.exportersApiUrl}/tasks/${id}/${this.commandStatus}`
    );
  }

  public exportNipts(
    exportType: ExportType,
    uri: string,
    countries: string[],
    filters: { [key: string]: string },
    password?: string,
    anonymous?: boolean
  ) {
    const body: any = {
      blobUri: uri,
      password,
      anonymous,
    };
    if (exportType === ExportType.FullInformation) {
      body.fullExport = true;
    }

    if (countries && !filters?.countries) {
      filters.countries = countries.join(',');
    }

    if (filters) {
      body.query = filters;
    }

    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${this.getExportCommand(exportType)}`,
      body
    );
  }

  public exportBatch(
    exportType: LaboratoryBatchExportType,
    blobUri: string,
    batchId: string
  ) {
    const body: any = {
      blobUri,
      batchId,
    };

    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${this.getExportCommand(exportType)}`,
      body
    );
  }

  public generateRequestForm(
    content: string | { filename: string; content: string }[],
    password = ''
  ) {
    return this.httpClient.post<{ data: string }>(this.html2pdfUrl, {
      content,
      password,
    });
  }

  public inPartnerWarehouseExport(
    uri: string,
    laboratory?: string,
    expiresFrom?: string,
    expiresTo?: string
  ): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandExportInPartnerWarehouseKits}`,
      { blobUri: uri, query: { expiresFrom, expiresTo, laboratory } }
    );
  }

  public doctorsExport(
    doctorIds: string[],
    uri: string
  ): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandExportDoctors}`,
      {
        blobUri: uri,
        doctors: doctorIds,
      }
    );
  }

  public allPartnersExport(
    uri: string,
    filters: any
  ): Observable<StatusResponse> {
    const body = {
      blobUri: uri,
      query: {
        ...filters,
      },
    };

    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandExportPartners}`,
      body
    );
  }

  public onlinePartnersExport(
    uri: string,
    filters: any
  ): Observable<StatusResponse> {
    const body = {
      blobUri: uri,
      query: {
        ...filters,
      },
    };

    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandExportOnlinePartners}`,
      body
    );
  }

  public doctorsByPartnersExport(
    uri: string,
    filters: any
  ): Observable<StatusResponse> {
    const body = {
      blobUri: uri,
      query: {
        ...filters,
      },
    };
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandExportDoctorsByPartners}`,
      body
    );
  }

  public plasmaExport(uri: string, runId: string): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandPlasmaExport}`,
      { blobUri: uri, runId }
    );
  }

  public bgiPlasmaExport(
    uri: string,
    runId: string,
    password: string
  ): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandBgiPlasmaExport}`,
      { blobUri: uri, runId, password }
    );
  }

  public halosExport(
    uri: string,
    runIds: string[]
  ): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandHalosExport}`,
      { blobUri: uri, runIds }
    );
  }

  public halosProExport(
    uri: string,
    runIds: string[]
  ): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandHalosProExport}`,
      { blobUri: uri, runIds }
    );
  }

  public tatExport(uri: string, runIds: string[]): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(
      `${this.exportersApiUrl}/v2/${EXPORTERS_API_CONSTANTS.commandTatExport}`,
      { blobUri: uri, runIds }
    );
  }

  private getExportCommand(exportType) {
    if (exportType === ExportType.Information) {
      return EXPORTERS_API_CONSTANTS.commandExportInformationSheet;
    } else if (exportType === ExportType.CancerscreenInformation) {
      return EXPORTERS_API_CONSTANTS.commandExportCancerscreenInformationSheet;
    } else if (exportType === ExportType.PatientData) {
      return EXPORTERS_API_CONSTANTS.commandExportPatientData;
    } else if (exportType === ExportType.BGIInformation) {
      return EXPORTERS_API_CONSTANTS.commandExportBgiInformationSheet;
    } else if (exportType === ExportType.FullInformation) {
      return EXPORTERS_API_CONSTANTS.commandExportNiptsFull;
    } else if (exportType === ExportType.Invoice) {
      return EXPORTERS_API_CONSTANTS.commandNiptsInvoiceExport;
    } else if (exportType === ExportType.Analytics) {
      return EXPORTERS_API_CONSTANTS.commandNiptsAnalyticsExport;
    } else if (exportType === ExportType.Report) {
      return EXPORTERS_API_CONSTANTS.commandNiptsReportExport;
    } else if (exportType === ExportType.N2DStatistics) {
      return EXPORTERS_API_CONSTANTS.commandN2DReportExport;
    } else if (exportType === ExportType.N2DCustomerOrders) {
      return EXPORTERS_API_CONSTANTS.commandN2DCustomerOrdersExport;
    } else if (exportType === ExportType.Logistics) {
      return EXPORTERS_API_CONSTANTS.commandLogisticsReportExport;
    } else if (exportType === ExportType.Insurance) {
      return EXPORTERS_API_CONSTANTS.commandInsuranceReportExport;
    } else if (exportType === ExportType.Operations) {
      return EXPORTERS_API_CONSTANTS.commandOperationsReportExport;
    } else if (exportType === ExportType.DigitalReports) {
      return EXPORTERS_API_CONSTANTS.commandDigitalReportsExport;
    } else if (exportType === ExportType.Orders) {
      return EXPORTERS_API_CONSTANTS.commandOrdersReportExport;
    } else if (exportType === ExportType.Consents) {
      return EXPORTERS_API_CONSTANTS.commandConsentsReportExport;
    } else if (exportType === ExportType.Statistics) {
      return EXPORTERS_API_CONSTANTS.commandStatisticsExport;
    } else if (exportType === ExportType.Payment) {
      return EXPORTERS_API_CONSTANTS.commandPaymentExport;
    } else if (exportType === ExportType.SamplesOverview) {
      return EXPORTERS_API_CONSTANTS.commandSamplesOverviewExport;
    } else if (exportType === ExportType.FollowUps) {
      return EXPORTERS_API_CONSTANTS.commandFollowUpsExport;
    } else if (exportType === LaboratoryBatchExportType.BatchSamples) {
      return EXPORTERS_API_CONSTANTS.commandExportBatchSamples;
    } else if (exportType === LaboratoryBatchExportType.BatchIssues) {
      return EXPORTERS_API_CONSTANTS.commandExportBatchIssues;
    } else if (exportType === LaboratoryBatchExportType.BatchLabels) {
      return EXPORTERS_API_CONSTANTS.commandExportBatchLabels;
    } else if (exportType === ExportType.OperationsReports) {
      return EXPORTERS_API_CONSTANTS.commandOperationsReportsExport;
    }

    return EXPORTERS_API_CONSTANTS.commandExportNipts;
  }
}
